import React from "react";

// Material Imports
import {
  Grid,
  Switch,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  Autocomplete,
  FormControlLabel,
  FormHelperText,
  Divider,
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers";

import { useGetDeviceListQuery } from "app/store/devices/deviceApi";
import { useFetchAccountListQuery } from "app/store/dashboard/doctor_analytic/api/doctorAnalyticApi";

import {
  GENDER_OPTIONS,
  GENDER_IDENTITY_OPTIONS,
  SEX_ASSIGNED_AT_BIRTH_OPTIONS,
  DEFAULT_NOTIFICATION_CHANNEL,
} from "static/constants";
import dayjs from "dayjs";
import { useGetManufacturersQuery } from "app/store/manufacturers/api/manufacturersApi";
import { getFormattedDate } from "utils/date";
import { DATE_FORMAT } from "static/date";
import NotificationChannels from "components/pages/transmissions/modal/patient/tabs/subscription/setting/notificationChannels/NotificationChannels";
import ReminderSchedule from "components/pages/transmissions/modal/patient/tabs/subscription/setting/reminderSchedule/ReminderSchedule";
import RPMDeviceInput from "./RPMDeviceInput";

const PatientEnrollForm = (props) => {
  const {
    patientData,
    allowInitialRPMModal,
    isLoading,
    setFieldValue,
    touched,
    errors,
  } = props;

  const { data: devices } = useGetDeviceListQuery();
  const { data: manufacturerList } = useGetManufacturersQuery();
  const { data: availableAccounts } = useFetchAccountListQuery();

  const isRPM = window.location.pathname.includes("patient/rpm");

  return (
    <>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <h3>Patient Information</h3>
        </Grid>
        <Grid item>
          <FormControlLabel
            control={
              <Switch
                name="active"
                color="secondary"
                defaultChecked={patientData.active}
                onChange={(e, c) => {
                  setFieldValue("active", c);
                }}
              />
            }
            label="Active Patient"
            labelPlacement="start"
          />
        </Grid>
      </Grid>

      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Grid item xs={12} sm={6}>
          <TextField
            variant="outlined"
            required
            fullWidth
            id="firstName"
            label="First Name"
            name="firstName"
            autoFocus
            value={patientData.firstName}
            onChange={(e) => {
              const value = e.target.value;
              setFieldValue("firstName", value);
            }}
            InputProps={
              isLoading && {
                readOnly: true,
              }
            }
            error={touched.firstName && !!errors.firstName}
            helperText={touched.firstName && errors.firstName}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            variant="outlined"
            required
            fullWidth
            id="lastName"
            label="Last Name"
            name="lastName"
            value={patientData.lastName}
            onChange={(e) => {
              const value = e.target.value;
              setFieldValue("lastName", value);
            }}
            InputProps={
              isLoading && {
                readOnly: true,
              }
            }
            error={touched.lastName && !!errors.lastName}
            helperText={touched.lastName && errors.lastName}
          />
        </Grid>
      </Grid>

      <Grid
        container
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Grid item xs={12} sm={6} md={4}>
          <FormControl
            variant="outlined"
            fullWidth
            error={touched.gender && Boolean(errors.gender)}
          >
            <InputLabel id="gender-label" style={{ background: "white" }}>
              Select Gender (Legal)
            </InputLabel>
            <Select
              id="gender"
              value={patientData.gender}
              name="gender"
              onChange={(e) => {
                const value = e.target.value;
                setFieldValue("gender", value);
              }}
              readOnly={isLoading}
            >
              {GENDER_OPTIONS.map((gender, index) => (
                <MenuItem value={gender.value} key={index}>
                  {gender.option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {touched.gender && Boolean(errors.gender) && (
            <FormHelperText error>{errors.gender} </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              id="genderIdentity-label"
              className="patient-enroll-modal__input-label--ellipse"
            >
              Select Gender Identity
            </InputLabel>
            <Select
              id="genderIdentity"
              value={patientData.genderIdentity}
              name="genderIdentity"
              onChange={(e) => {
                const value = e.target.value;
                setFieldValue("genderIdentity", value);
              }}
              readOnly={isLoading}
            >
              {GENDER_IDENTITY_OPTIONS.map((gender, index) => (
                <MenuItem value={gender.value} key={index}>
                  {gender.option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel
              id="sexAssignedAtBirth-label"
              className="patient-enroll-modal__input-label--ellipse"
            >
              Select Sex Assigned At Birth
            </InputLabel>
            <Select
              id="sexAssignedAtBirth"
              value={patientData.sexAssignedAtBirth}
              name="sexAssignedAtBirth"
              onChange={(e) => {
                const value = e.target.value;
                setFieldValue("sexAssignedAtBirth", value);
              }}
              readOnly={isLoading}
            >
              {SEX_ASSIGNED_AT_BIRTH_OPTIONS.map((gender) => (
                <MenuItem value={gender.value}>{gender.option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Grid
        container
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <DatePicker
              required
              inputVariant="outlined"
              margin="normal"
              autoOk
              id="filter_date"
              label="Date of Birth"
              format={DATE_FORMAT.DEFAULT}
              placeholder={DATE_FORMAT.DEFAULT}
              KeyboardButtonProps={{
                "aria-label": "pick date",
              }}
              value={patientData.dob ? dayjs(patientData.dob) : null}
              onChange={(date) => {
                const formattedDate = getFormattedDate(date);

                setFieldValue("dob", formattedDate);
              }}
              readOnly={isLoading}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: touched.dob && Boolean(errors.dob),
                  helperText: touched.dob && errors.dob,
                },
              }}
              maxDate={dayjs(new Date())}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl
            variant="outlined"
            fullWidth
            required
            error={touched.timezone && Boolean(errors.timezone)}
          >
            <InputLabel id="timezone-label" style={{ background: "white" }}>
              Select Timezone
            </InputLabel>
            <Select
              id="timezone"
              value={patientData.timezone}
              name="timezone"
              onChange={(e) => {
                const value = e.target.value;

                setFieldValue("timezone", value);
              }}
              readOnly={isLoading}
            >
              <MenuItem value="pacific">Pacific</MenuItem>
              <MenuItem value="mountain">Mountain</MenuItem>
              <MenuItem value="central">Central</MenuItem>
              <MenuItem value="eastern">Eastern</MenuItem>
            </Select>
          </FormControl>
          {touched.timezone && Boolean(errors.timezone) && (
            <FormHelperText error>{errors.timezone} </FormHelperText>
          )}
        </Grid>
      </Grid>

      <FormControl
        variant="outlined"
        fullWidth
        style={{ marginTop: 15 }}
        required
        error={touched.account && Boolean(errors.account)}
      >
        <InputLabel id="account-label" style={{ background: "white" }}>
          Select Account
        </InputLabel>
        <Select
          id="account"
          value={patientData.account}
          name="account"
          onChange={(e) => {
            const value = e.target.value;
            const selectedAccount = availableAccounts.find(
              (acc) => acc.name === value
            );

            const defaultSubscribeSetting = selectedAccount?.settings
              ?.subscribe_setting || {
              notification_channels: DEFAULT_NOTIFICATION_CHANNEL,
            };
            setFieldValue("account", value);
            setFieldValue("subscribe_setting", defaultSubscribeSetting);
          }}
          readOnly={isLoading}
        >
          {availableAccounts?.length > 0 &&
            availableAccounts.map(({ name }) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      {touched.account && Boolean(errors.account) && (
        <FormHelperText error>{errors.account} </FormHelperText>
      )}

      <Divider style={{ marginTop: "32px", marginBottom: "32px" }} />
      {!isRPM && (
        <>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            style={{ marginTop: 15 }}
          >
            <Grid item>
              <h3>Device Information</h3>
            </Grid>

            <Grid item>
              <FormControlLabel
                control={
                  <Switch
                    name="activeDevice"
                    color="secondary"
                    defaultChecked={patientData.activeDevice}
                    onChange={(e, c) => {
                      setFieldValue("activeDevice", c);
                    }}
                  />
                }
                label="Active Device"
                labelPlacement="start"
              />
            </Grid>
          </Grid>

          <Grid
            container
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
            style={{ marginTop: "10px" }}
          >
            <Grid item xs={12} sm={6}>
              <FormControl
                variant="outlined"
                fullWidth
                required
                error={touched.manufacturer && Boolean(errors.manufacturer)}
              >
                <InputLabel
                  id="manufacturer-label"
                  style={{ background: "white" }}
                >
                  Select Manufacturer
                </InputLabel>
                <Select
                  id="manufacturer"
                  value={patientData.manufacturer}
                  name="manufacturer"
                  onChange={(e) => {
                    const value = e.target.value;

                    setFieldValue("manufacturer", value);
                  }}
                  readOnly={isLoading}
                >
                  {manufacturerList?.map((manufacturer) => (
                    <MenuItem key={manufacturer.name} value={manufacturer.name}>
                      {manufacturer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {touched.manufacturer && Boolean(errors.manufacturer) && (
                <FormHelperText error>{errors.manufacturer} </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                inputVariant="outlined"
                autoOk
                id="filter_date"
                label="Date of Implant"
                format="MM/DD/YYYY"
                name="doi"
                KeyboardButtonProps={{
                  "aria-label": "pick date",
                }}
                placeholder="MM/DD/YYYY"
                value={patientData.doi ? dayjs(patientData.doi) : null}
                onChange={(date) => {
                  const formattedDate = getFormattedDate(date);

                  setFieldValue("doi", formattedDate);
                }}
                readOnly={isLoading}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: patientData?.rpm_devices?.length === 0,
                    error: touched.doi && Boolean(errors?.doi),
                    helperText: touched.doi && errors.doi,
                  },
                }}
                maxDate={dayjs(new Date())}
              />
            </Grid>
          </Grid>

          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Grid item xs={12} sm={6}>
              <FormControl style={{ width: "100%", marginTop: "8px" }}>
                <Autocomplete
                  id="modelName"
                  name="modelName"
                  clearOnBlur
                  value={
                    patientData?.modelName
                      ? devices?.find(
                          (item) =>
                            item?.modelDisplayName === patientData?.modelName
                        )
                      : null
                  }
                  filterSelectedOptions
                  limitTags={2}
                  options={
                    devices?.filter((item) => item?.modelDisplayName) || []
                  }
                  getOptionLabel={(option) => option?.modelDisplayName}
                  noOptionsText="Enter to create a new model name"
                  onChange={(e, newValue) => {
                    setFieldValue(
                      "modelName",
                      newValue?.modelDisplayName || ""
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Model Name"
                      variant="outlined"
                      placeholder="Model Name"
                      required
                      InputProps={{
                        ...params.InputProps,
                        readOnly: isLoading ? true : false,
                      }}
                      error={touched.modelName && Boolean(errors.modelName)}
                      helperText={touched.modelName && errors.modelName}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="modelNumber"
                label="Serial Number"
                name="modelNumber"
                value={patientData.modelNumber || ""}
                onChange={(e) => {
                  const regex = /^[A-Za-z0-9]*$/;
                  if (e.target.value === "" || regex.test(e.target.value)) {
                    setFieldValue("modelNumber", e.target.value);
                  }
                }}
                InputProps={
                  isLoading && {
                    readOnly: true,
                  }
                }
                error={touched.modelNumber && Boolean(errors.modelNumber)}
                helperText={touched.modelNumber && errors.modelNumber}
              />
            </Grid>
          </Grid>

          <RPMDeviceInput
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            rpmDevices={patientData?.rpm_devices}
            allowInitialRPMModal={allowInitialRPMModal}
          />

          <Divider style={{ marginTop: "32px", marginBottom: "32px" }} />

          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            style={{ marginTop: 15 }}
          >
            <Grid item className="mb-2">
              <h3>Subscribe Setting</h3>
            </Grid>
            <Grid container>
              <NotificationChannels
                isEdit={true}
                isPatientEnroll={true}
                handleChange={(_, value) => {
                  setFieldValue(
                    `subscribe_setting.notification_channels`,
                    value
                  );
                }}
                values={patientData?.subscribe_setting?.notification_channels}
              />
            </Grid>
            <Grid container style={{ marginBottom: "32px" }}>
              <ReminderSchedule
                isEdit={true}
                isPatientEnroll={true}
                values={patientData?.subscribe_setting}
                handleChange={(key, value) => {
                  setFieldValue(`subscribe_setting.${key}`, value);
                }}
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default PatientEnrollForm;
